import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDistricts } from "@/api/districts/district-queries";
import { useAssignSourceToDistrict } from "@/api/districts/mutations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	selectedSns: string[];
	onSuccess?: () => void;
};

export function BulkAssignSourceToDistrictDialog({
	open,
	onOpenChange,
	selectedSns,
	onSuccess,
}: Props) {
	const { t } = useTranslation();
	const [districtId, setDistrictId] = useState<string>("");

	const assign = useAssignSourceToDistrict();
	const { data: districts } = useDistricts();

	useEffect(() => {
		if (open) setDistrictId("");
	}, [open]);

	function handleSubmit() {
		if (!districtId || selectedSns.length === 0) return;
		assign.mutate(
			{ source_sns: selectedSns, district_id: Number(districtId) },
			{
				onSuccess: () => {
					toast.success(t("mqttSources.assignToDistrictSuccess"));
					onOpenChange(false);
					onSuccess?.();
				},
				onError: () => {
					toast.error(t("mqttSources.assignToDistrictError"));
				},
			},
		);
	}

	const canSubmit = !!districtId && selectedSns.length > 0 && !assign.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{t("mqttSources.bulkAssignToDistrictTitle")}
					</DialogTitle>
					<DialogDescription>
						{t("mqttSources.bulkAssignToDistrictDescription")}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="selected-gateways">
							{t("mqttSources.selectedCount", { count: selectedSns.length })}
						</Label>
						<div className="flex flex-wrap gap-1.5">
							{selectedSns.map((sn) => (
								<Badge key={sn} variant="secondary">
									{sn}
								</Badge>
							))}
						</div>
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="bulk-district-select">
							{t("mqttSources.colDistrict")}
						</Label>
						<Select value={districtId} onValueChange={setDistrictId}>
							<SelectTrigger className="w-full">
								<SelectValue placeholder={t("mqttSources.selectDistrict")} />
							</SelectTrigger>
							<SelectContent>
								{(districts ?? []).map((d) => (
									<SelectItem key={d.id} value={String(d.id)}>
										{d.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={assign.isPending}
					>
						{t("mqttSources.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{t("mqttSources.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
